import React, { useMemo, useState, Fragment, useEffect } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@coverme/icons";
import "./MiniFacesheet.scss";
import { MiniFacesheetDemographicInfo } from "./MiniFacesheetDemographicInfo";
import { MiniFacesheetPersonal } from "./MiniFacesheetPersonal";
import { MiniFacesheetHousehold } from "./MiniFacesheetHousehold";
import { MiniFacesheetIncome } from "./MiniFacesheetIncome";
import { MiniFacesheetOtherFinancial } from "./MiniFacesheetOtherFinancial";
import { MiniFacesheetCoverage } from "./MiniFacesheetCoverage";
import { OpenFacesheetIcon } from "./OpenFacesheetIcon";
import {
    calculateTotalAllowableIncome,
    createHouseholdMembersFromInterview,
    getApplicableHouseholds,
    getCountableAndNonCountablePersons,
} from "../../services/magiServices";
import { MiniFacesheetNotes } from "../Notes/MiniFacesheetNotes";
import { LoadingBar } from "../LoadingComponents/LoadingBar";
import { MiniFacesheetDocumentItem } from "../Documents";
import { MiniFacesheetSection } from "./MiniFacesheetSection";
import { connect } from "react-redux";
import { MiniFacesheetVerification } from "./MiniFacesheetVerification";
import { formatScore } from "../../services/scoreFormatter";
import { getScoresWithReasons } from "../../services/fitRiskReliabilityScoreService";
import { some } from "lodash-es";

const MiniFacesheet = ({
    interview,
    encounterId = "",
    households,
    notes,
    person,
    documents,
    programs,
    overallScore,
    customerFeatures,
    lookups,
}) => {
    const [isOpen, toggleFacesheet] = useState(true);
    const [encounterTag, setEncounterTag] = useState(null);

    const interviewHouseholdMembers = createHouseholdMembersFromInterview(interview);
    const patientId = interview.entry.patient.personId;
    const patientDocuments = documents.data[patientId] == null ? [] : documents.data[patientId];

    const applicableHouseholds = useMemo(() => getApplicableHouseholds(households), [households]);

    useEffect(() => {
        if (
            lookups?.automatedTags !== undefined &&
            lookups?.automatedTags !== null &&
            lookups?.manualTags !== undefined &&
            lookups?.manualTags !== null &&
            interview.encounterTag !== "" &&
            interview.encounterTag !== undefined &&
            interview.encounterTag !== null
        ) {
            let allTags = [...lookups.automatedTags, ...lookups.manualTags];

            let tagOption = allTags.filter(x => x.id === interview.encounterTag);
            if (tagOption?.length > 0) {
                setEncounterTag(tagOption[0].tagName);
            }
        }
    }, [lookups, interview, interview.encounterTag]);

    const isTagsEnabled = some(customerFeatures, x => x === "Tags");
    const responsibleParty = interview.entry.isResponsiblePartyDifferentFromPatient
        ? interview.entry.responsibleParty
        : interview.entry.patient;

    const scoreDetails = useMemo(() => {
        if (programs == null || programs.length === 0) {
            return;
        }

        let scoresWithReasons = getScoresWithReasons(
            programs,
            responsibleParty,
            interview.encounterStatus !== "Complete"
        );
        return {
            scores: scoresWithReasons.scores,
            isIncomplete: scoresWithReasons.isIncomplete,
        };
    }, [programs]);

    const getFacesheetIcon = () => {
        if (isOpen) {
            return <span className="mini-facesheet__is-open">&times;</span>;
        } else {
            return (
                <span className="mini-facesheet__is-closed">
                    Facesheet{" "}
                    <span className="mini-facesheet__right-arrow">
                        <Icon icon={"arrow"} color={"blue"} height={9} width={9} />
                    </span>
                </span>
            );
        }
    };
    return (
        <div className={`mini-facesheet ${isOpen ? "open" : "closed"}`}>
            <div className="mini-facesheet__content">
                {isOpen && (
                    <Link to={`/interview/${encounterId}/details/facesheet`} className="mini-facesheet__link">
                        <OpenFacesheetIcon />
                    </Link>
                )}

                <div>
                    <div className="mini-facesheet__details">
                        {programs[0]?.assistanceCategory !== "Medicaid Long Term Care" && (
                            <MiniFacesheetDemographicInfo
                                entry={interview.entry}
                                score={
                                    scoreDetails?.isIncomplete === true &&
                                    (scoreDetails?.scores?.overallScore < 0
                                        ? "*"
                                        : formatScore(scoreDetails?.scores?.overallScore))
                                }
                            />
                        )}
                        {programs[0]?.assistanceCategory === "Medicaid Long Term Care" && (
                            <MiniFacesheetDemographicInfo entry={interview.entry} score={programs[0].score?.value} />
                        )}
                    </div>
                    <MiniFacesheetSection title="Personal">
                        <MiniFacesheetPersonal entry={interview.entry} />
                    </MiniFacesheetSection>
                    <MiniFacesheetSection title="Verification">
                        <MiniFacesheetVerification entry={interview.entry} verifications={interview.verifications} />
                    </MiniFacesheetSection>

                    {interview?.coverage?.coverageSummary?.personCoverages?.length > 0 && (
                        <MiniFacesheetSection title="Coverage">
                            <MiniFacesheetCoverage coverages={interview.coverage?.coverageSummary?.personCoverages} />
                        </MiniFacesheetSection>
                    )}

                    {applicableHouseholds.map((household, i) => {
                        const { countable, nonCountable } = getCountableAndNonCountablePersons(
                            interviewHouseholdMembers,
                            household
                        );

                        const totalIncome = calculateTotalAllowableIncome(household);
                        return (
                            <React.Fragment key={i}>
                                <MiniFacesheetSection
                                    title={`${household.householdType.name} Household (${household.countsAs})`}
                                >
                                    <MiniFacesheetHousehold
                                        countableMembers={countable}
                                        nonCountableMembers={nonCountable}
                                        totalCountable={household.countsAs}
                                        household={household}
                                        interview={interview}
                                    />
                                </MiniFacesheetSection>

                                <MiniFacesheetSection
                                    title={`${household.householdType.name} Income ($${totalIncome.toFixed(2)})`}
                                >
                                    <MiniFacesheetIncome
                                        countableMembers={countable}
                                        interview={interview}
                                        magiHousehold={household}
                                    />
                                </MiniFacesheetSection>
                            </React.Fragment>
                        );
                    })}

                    {(interview.assets?.assets?.length > 0 ||
                        interview.liabilities.expenses?.length > 0 ||
                        interview.liabilities.debts?.length > 0) && (
                        <MiniFacesheetSection title="Other Financial">
                            <MiniFacesheetOtherFinancial
                                expenses={interview.liabilities.expenses}
                                assets={interview.assets?.assets}
                                debts={interview.liabilities.debts}
                                financialAssistance={interview.financialAssistance}
                            />
                        </MiniFacesheetSection>
                    )}

                    <MiniFacesheetSection title="Documents">
                        <div className="mini-facesheet__documents-content">
                            <LoadingBar isLoading={documents.isInitialized === false || documents.isLoading === true}>
                                {patientDocuments.map((d, i) => {
                                    return <MiniFacesheetDocumentItem key={i} {...d} personId={patientId} />;
                                })}
                            </LoadingBar>
                        </div>
                    </MiniFacesheetSection>

                    {notes !== undefined && notes !== null && notes.length > 0 && (
                        <MiniFacesheetSection title={`Notes (${notes?.length || 0})`}>
                            <MiniFacesheetNotes
                                lengthToTruncate={100}
                                onNoteAdded={() => {}}
                                isNotesLoading={false}
                                notes={notes}
                            />
                        </MiniFacesheetSection>
                    )}

                    {isTagsEnabled && encounterTag !== null && encounterTag !== "" && encounterTag !== undefined && (
                        <MiniFacesheetSection title={`Tag: ${encounterTag}`} isExpandable={false} isClickable={false} />
                    )}
                </div>
            </div>
            <span
                className="mini-facesheet__toggle"
                onClick={() => {
                    toggleFacesheet(!isOpen);
                }}
            >
                {getFacesheetIcon()}
            </span>
        </div>
    );
};

export const MiniFacesheetContainer = connect(state => {
    const patientId = state.interview?.entry?.patient?.personId;
    let underlyingPerson = null;
    if (patientId) {
        underlyingPerson = state.person.data[patientId];
    }
    return {
        interview: state.interview,
        encounterId: state.interview.encounterId,
        households: state.households.data,
        notes: state.notes.data[underlyingPerson?.globalId],
        person: underlyingPerson,
        documents: state.documents,
        coverage: state.coverage,
        programs: state.programs.data,
        customerFeatures: state.customer?.data?.features,
        lookups: state.interviewLookups.data || {},
    };
})(MiniFacesheet);
